import { Box, Chip, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { DataGrid } from 'Components/LifeDataGrid'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { convertToUserDateFormate } from 'utils/helpers'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// Source de l'opération budget (trame FK editable=False du modèle) :
// échéance de contrat, tâche, financement… — sinon saisie manuelle.
const rowSource = (row, t) => {
  if (row.Contractid) {
    return {
      label: t('finance.monBudget.sourceContract', 'Contrat'),
      tip: row.contract_name || '',
      bg: 'rgba(74,222,128,0.18)',
      color: '#15803d',
    }
  }
  if (row.Taskid) {
    return { label: t('finance.monBudget.sourceTask', 'Tâche'), tip: '', bg: 'rgba(126,156,219,0.20)', color: '#3b5bdb' }
  }
  if (row.Financementid) {
    return { label: t('finance.monBudget.sourceFinancing', 'Financement'), tip: '', bg: 'rgba(251,191,36,0.20)', color: '#b45309' }
  }
  if (row.opfi_id) {
    return { label: t('finance.monBudget.sourceImport', 'Import'), tip: '', bg: 'rgba(148,163,184,0.25)', color: '#475569' }
  }
  return null
}

function SourceChip({ row, t }) {
  const src = rowSource(row, t)
  if (!src) return <>{t('finance.monBudget.sourceManual', 'Manuel')}</>
  const chip = (
    <Chip size="small" label={src.label} sx={{ height: 20, fontSize: 11, bgcolor: src.bg, color: src.color, fontWeight: 600 }} />
  )
  return src.tip ? <Tooltip title={src.tip}>{chip}</Tooltip> : chip
}

function ListOperations({ fetchOperations, operations, setSelectedRows }) {
  const siteUser = useSelector((store) => store.user)
  const { t } = useTranslation('layout') // LAP-733 (GE022)

  const [rows, setRows] = useState(20)
  const [page, setPage] = useState(0)
  const theme = useTheme()
  const [modifiedColumns, setModifiedColumns] = useState([])
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  useEffect(() => {
    // setTTs(!tts)
    if (isMobile) {
      setModifiedColumns([
        {
          field: 'Date',
          headerName: t('finance.common.date'),
          width: 190,
          renderCell: (params) => (
            <Box py={2} mt={1}>
              <Box fontWeight={600}>{convertToUserDateFormate(siteUser.user, params.row.Date)}</Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineLibelle')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Libelle}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineCategory')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.Category}
                </Box>
              </Box>
              <Box>
                <Box component={'span'}>{t('finance.monBudget.inlineSubCategory')}:</Box>{' '}
                <Box component={'span'} fontWeight={600}>
                  {params.row.SousCategorie}
                </Box>
              </Box>
              {rowSource(params.row, t) && (
                <Box mt={0.5}>
                  <SourceChip row={params.row} t={t} />
                </Box>
              )}
            </Box>
          ),
        },
        {
          field: 'Montant',
          headerName: t('finance.monBudget.columnAmount'),
          width: 150,
          renderCell: (params) => (
            <Box py={2}>
              <Box component={'span'} color={params.row.Montant >= 0 ? 'green' : 'red'}>
                {params.row.Montant}
              </Box>
              <Box component={'span'} fontWeight={600}>
                {params.row.Devise}
              </Box>
            </Box>
          ),
        },
      ])
    } else {
      setModifiedColumns(columns)
    }
  }, [isMobile])

  const handleRowsChange = (newPageSize) => {
    setRows(newPageSize)
    let url = `&limit=${newPageSize}&offset=${page}`
    fetchOperations(url)
  }
  const handlePageChange = (newPage) => {
    setPage(newPage * rows)
    let url = `&limit=${rows}&offset=${newPage * rows}`
    fetchOperations(url)
  }

  function DataGridTitle() {
    return (
      <Box style={{ width: '100%', display: 'flex' }} mt={2}>
        <Typography sx={{ color: 'black !important', fontWeight: 700, marginLeft: 2 }} variant="h5">
          {t('finance.monBudget.operationsCount', { count: operations.count ? operations.count : 0 })}
        </Typography>
      </Box>
    )
  }
  // const processRowUpdate = useCallback((newRow, oldRow) => {
  //   console.log(newRow)
  // }, [])

  const columns = [
    {
      field: 'Date',
      headerName: t('finance.common.date'),
      width: 100,
      // valueGetter: (params) => convertToUserDateFormate(siteUser.user, params.row.Date),
      renderCell: (params) => <>{convertToUserDateFormate(siteUser.user, params.row.Date)}</>,
    },
    {
      field: 'Libelle',
      headerName: t('finance.monBudget.columnLibelle'),
      width: 320,
      editable: true,
    },
    {
      field: 'Classification',
      headerName: t('finance.monBudget.columnClassification'),
      width: 190,
      editable: true,
    },
    {
      field: 'Category',
      headerName: t('finance.monBudget.columnCategory'),
      width: 110,
      editable: true,
    },
    {
      field: 'SousCategorie',
      headerName: t('finance.monBudget.columnSubCategory'),
      width: 140,
      editable: true,
    },
    {
      field: 'Montant',
      headerName: t('finance.monBudget.columnAmount'),
      width: 80,
      editable: true,
      renderCell: (params) => <>{params.row.Montant}</>,
    },
    {
      field: 'Devise',
      headerName: t('finance.monBudget.columnCurrency'),
      width: 60,
      editable: true,
    },
    {
      field: 'source',
      headerName: t('finance.monBudget.columnSource', 'Source'),
      width: 110,
      sortable: false,
      renderCell: (params) => <SourceChip row={params.row} t={t} />,
    },
  ]
  return (
    <div
      style={{
        height: 750,
        width: '100% ',
        background: 'white',
        borderRadius: '5px',
      }}
    >
      <DataGrid
        sx={{
          '.MuiTablePagination-toolbar': {
            alignItems: 'baseline',
          },
        }}
        rows={operations.results ? operations.results : []}
        columns={modifiedColumns}
        pageSize={rows}
        getRowHeight={() => (isMobile ? 'auto' : 50)}
        rowCount={operations.count ? operations.count : 0}
        onPageSizeChange={handleRowsChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 40, 80, 100]}
        pagination
        onSelectionModelChange={(itm) => setSelectedRows([...itm])}
        paginationMode="server"
        checkboxSelection
        disableSelectionOnClick
        components={{ Toolbar: DataGridTitle }}
        // processRowUpdate={processRowUpdate}
        // experimentalFeatures={{ newEditingApi: true }}
      />
    </div>
  )
}
export default ListOperations
